var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/dialog.ts
var DEFAULT_CONFIRM_LABEL = "\uD655\uC778";
var DEFAULT_TITLE = "\uD655\uC778";
function resolveTitle(title) {
  if (title === void 0) {
    return DEFAULT_TITLE;
  }
  return title === false ? "" : title;
}
__name(resolveTitle, "resolveTitle");
var DEFAULT_CANCEL_LABEL = "\uCDE8\uC18C";
function normalizeEmphasis(input) {
  if (!input) {
    return [];
  }
  return (Array.isArray(input) ? input : [
    input
  ]).filter(Boolean);
}
__name(normalizeEmphasis, "normalizeEmphasis");
function splitEmphasis(message, marks) {
  const valid = marks.filter(Boolean);
  if (valid.length === 0) {
    return [
      {
        text: message,
        bold: false
      }
    ];
  }
  const segments = [];
  let rest = message;
  while (rest) {
    let at = -1;
    let mark = "";
    for (const candidate of valid) {
      const found = rest.indexOf(candidate);
      if (found >= 0 && (at < 0 || found < at)) {
        at = found;
        mark = candidate;
      }
    }
    if (at < 0) {
      segments.push({
        text: rest,
        bold: false
      });
      break;
    }
    if (at > 0) {
      segments.push({
        text: rest.slice(0, at),
        bold: false
      });
    }
    segments.push({
      text: mark,
      bold: true
    });
    rest = rest.slice(at + mark.length);
  }
  return segments;
}
__name(splitEmphasis, "splitEmphasis");
var host = null;
function registerDialogHost(state) {
  host = state;
}
__name(registerDialogHost, "registerDialogHost");
function hasDialogHost() {
  return host !== null;
}
__name(hasDialogHost, "hasDialogHost");
function resetDialogHost() {
  host = null;
}
__name(resetDialogHost, "resetDialogHost");
function toRequest(opts) {
  return typeof opts === "string" ? {
    message: opts
  } : opts;
}
__name(toRequest, "toRequest");
function assignBodyExtras(state, req) {
  state.details = req.details ?? [];
  state.table = req.table ?? null;
  state.notice = req.notice ?? null;
  state.footnote = req.footnote ?? "";
}
__name(assignBodyExtras, "assignBodyExtras");
function dismissOpenDialog() {
  if (!host?.open) {
    return false;
  }
  const resolve = host.resolver;
  const result = host.kind === "choice" ? "cancel" : host.kind === "prompt" ? null : false;
  host.open = false;
  host.resolver = null;
  resolve?.(result);
  return true;
}
__name(dismissOpenDialog, "dismissOpenDialog");
function showAlert(opts) {
  const req = toRequest(opts);
  if (!host) {
    return;
  }
  host.resolver?.(false);
  host.kind = "alert";
  host.reportable = req.tone === "danger" && typeof document !== "undefined" && !!document.querySelector("[data-cs-report-host]");
  host.title = resolveTitle(req.title);
  host.message = req.message;
  host.confirmLabel = req.confirmLabel ?? DEFAULT_CONFIRM_LABEL;
  host.tone = req.tone ?? "default";
  host.emphasis = normalizeEmphasis(req.emphasis);
  assignBodyExtras(host, req);
  host.resolver = null;
  host.open = true;
}
__name(showAlert, "showAlert");
function showError(opts) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("hereby:cs-report-precapture"));
  }
  showAlert({
    ...toRequest(opts),
    tone: "danger"
  });
}
__name(showError, "showError");
function askConfirm(opts) {
  const req = toRequest(opts);
  if (!host) {
    return Promise.resolve(true);
  }
  const state = host;
  state.resolver?.(false);
  return new Promise((resolve) => {
    state.kind = "confirm";
    state.reportable = false;
    state.title = resolveTitle(req.title);
    state.message = req.message;
    state.confirmLabel = req.confirmLabel ?? DEFAULT_CONFIRM_LABEL;
    state.cancelLabel = req.cancelLabel ?? DEFAULT_CANCEL_LABEL;
    state.tone = req.tone ?? "default";
    state.emphasis = normalizeEmphasis(req.emphasis);
    assignBodyExtras(state, req);
    state.resolver = resolve;
    state.open = true;
  });
}
__name(askConfirm, "askConfirm");
function askChoice(opts) {
  if (!host) {
    return Promise.resolve("cancel");
  }
  const state = host;
  state.resolver?.(false);
  return new Promise((resolve) => {
    state.kind = "choice";
    state.reportable = false;
    state.title = resolveTitle(opts.title);
    state.message = opts.message;
    state.confirmLabel = opts.confirmLabel ?? DEFAULT_CONFIRM_LABEL;
    state.altLabel = opts.altLabel;
    state.tone = opts.tone ?? "default";
    state.emphasis = normalizeEmphasis(opts.emphasis);
    assignBodyExtras(state, opts);
    state.resolver = (v) => {
      resolve(v === "confirm" || v === "alt" ? v : "cancel");
    };
    state.open = true;
  });
}
__name(askChoice, "askChoice");
function showPrompt(opts) {
  const req = typeof opts === "string" ? {
    message: opts
  } : opts;
  if (!host) {
    return Promise.resolve(null);
  }
  const state = host;
  state.resolver?.(false);
  return new Promise((resolve) => {
    state.kind = "prompt";
    state.inputType = req.inputType ?? "text";
    state.reportable = false;
    state.title = resolveTitle(req.title);
    state.message = req.message;
    state.value = req.value ?? "";
    state.placeholder = req.placeholder ?? "";
    state.requireValue = req.requireValue ?? false;
    state.confirmLabel = req.confirmLabel ?? DEFAULT_CONFIRM_LABEL;
    state.cancelLabel = req.cancelLabel ?? DEFAULT_CANCEL_LABEL;
    state.tone = req.tone ?? "default";
    state.emphasis = normalizeEmphasis(req.emphasis);
    assignBodyExtras(state, req);
    state.resolver = (result) => {
      state.value = "";
      resolve(typeof result === "string" ? result : null);
    };
    state.open = true;
  });
}
__name(showPrompt, "showPrompt");
function createDialogStore() {
  return {
    open: false,
    kind: "alert",
    title: "",
    message: "",
    confirmLabel: DEFAULT_CONFIRM_LABEL,
    cancelLabel: DEFAULT_CANCEL_LABEL,
    altLabel: "",
    tone: "default",
    emphasis: [],
    details: [],
    table: null,
    notice: null,
    footnote: "",
    value: "",
    inputType: "text",
    placeholder: "",
    requireValue: false,
    reportable: false,
    resolver: null,
    /**
    * Alpine 이 스토어 등록 직후 호출한다. 여기서의 `this` 는 반응형 프록시라,
    * 팩토리가 돌려준 원본이 아니라 이 프록시를 브리지에 등록해야 모듈에서
    * 바꾼 값이 화면에 반영된다.
    */
    init() {
      registerDialogHost(this);
    },
    /** 본문 조각(부분 굵게) — 템플릿이 x-for 로 돌면서 x-text 로 그린다. */
    get messageSegments() {
      return splitEmphasis(this.message ?? "", this.emphasis ?? []);
    },
    /** HTML 안 Alpine 식에서 쓰는 짧은 이름 — `await $store.dialog.ask('...')`. */
    ask(opts) {
      return askConfirm(opts);
    },
    choose(opts) {
      return askChoice(opts);
    },
    alert(opts) {
      showAlert(opts);
    },
    prompt(opts) {
      return showPrompt(opts);
    },
    /** requireValue 인데 입력이 비었으면 확인을 막는다(버튼 비활성 + Enter 무시). */
    get confirmDisabled() {
      return this.kind === "prompt" && this.requireValue && this.value.trim() === "";
    },
    /** 확인 — confirm 은 true, prompt 는 입력값(trim), choice 는 "confirm", alert 는 그냥 닫기. */
    accept() {
      if (this.confirmDisabled) {
        return;
      }
      if (this.kind === "choice") {
        this.settle("confirm");
        return;
      }
      this.settle(this.kind === "prompt" ? this.inputType === "password" ? this.value : this.value.trim() : true);
    },
    /**
    * 오류 얼럿의 「리포트 보내기」 — 다이얼로그를 닫고 CS 리포트 모달을 연다.
    * 오류 스냅샷은 SDK 수집기(마지막 오류)와 예비 캡처가 이미 들고 있다.
    */
    report() {
      this.settle(false);
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("hereby:cs-report-open"));
      }
    },
    /** 선택형의 가운데 버튼(대안). 다른 종류에서는 호출되지 않는다. */
    acceptAlt() {
      if (this.kind !== "choice") {
        return;
      }
      this.settle("alt");
    },
    /**
    * 취소 · Esc — confirm 은 false, prompt 는 null, choice 는 "cancel".
    * 선택형에는 취소 버튼이 없으므로 이 길은 Esc 전용이다.
    */
    cancel() {
      if (this.kind === "choice") {
        this.settle("cancel");
        return;
      }
      this.settle(this.kind === "prompt" ? null : false);
    },
    settle(result) {
      if (!this.open) {
        return;
      }
      this.open = false;
      const resolve = this.resolver;
      this.resolver = null;
      resolve?.(result);
    }
  };
}
__name(createDialogStore, "createDialogStore");
export {
  askChoice,
  askConfirm,
  createDialogStore,
  dismissOpenDialog,
  hasDialogHost,
  normalizeEmphasis,
  registerDialogHost,
  resetDialogHost,
  showAlert,
  showError,
  showPrompt,
  splitEmphasis
};
//# sourceMappingURL=dialog.js.map